import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {useAuth} from '../../Auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import styles from '../Universidades/TablaCrud.module.css';
import ExportXLSX from './ExportPDF';
import { IoIosAddCircleOutline, IoMdCreate } from 'react-icons/io';
import { FaArrowLeft, FaArrowRight, FaFilter, FaCheck, FaTimes } from 'react-icons/fa';
import AniadirUsuario from './AniadirUsuario';

const TablaUsuarios = () => {
  const {getToken, clearAuth} = useAuth(); 
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [modalUsuario, setModalUsuario] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null); 
  const [newUserType, setNewUserType] = useState(''); 
  const [updatingUserId, setUpdatingUserId] = useState(null); 
  const usuariosPorPagina = 10;
  const abortControllerRef = useRef(null);
  const navigate = useNavigate();
  const token = getToken(); 

  const tiposUsuario = { 
    1: 'Estudiante', 
    2: 'Administrador', 
    3: 'Tutor', 
    4: 'Bloqueado'
  }; 

  const [editingStatusUserId, setEditingStatusUserId] = useState(null); 
  const [newUserStatus, setNewUserStatus] = useState('1'); 
  const [updatingStatusUserId, setUpdatingStatusUserId] = useState(null); 

  const estadosUsuario = { 
    '1' : 'Activo', 
    '0' : 'Inactivo'
  }; 

  const handleEditUserStatus = useCallback((userId, currentStatus) => { 
    setEditingStatusUserId(userId); 
    setNewUserStatus(currentStatus?.toString()); 
  }, []); 

  const updateUserStatus = useCallback(async (userId, newStatus) => {
    try { 
      setUpdatingStatusUserId(userId); 
      const token = localStorage.getItem('authToken'); 
      const response = await fetch (`${process.env.REACT_APP_API_URL}/admin/usuarios/${userId}/status`, {
        method: 'PUT', 
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json'
        }, 
        body: JSON.stringify({estado: parseInt(newStatus)})
      }); 
      if (!response.ok) { 
        throw new Error('Error al actualizar estado del usuario'); 
      }
      setUsuarios(prevUsuarios =>
        prevUsuarios.map(usuario => 
          usuario.us_clave === userId ? { ...usuario, us_status: parseInt(newStatus)} : usuario
        )
      ); 
      setEditingStatusUserId(null); 
      setNewUserStatus(''); 
    } catch (error) { 
      console.error('Error al actualizar estado de usuario: ', error); 
      setError('Error al actualizar el estado del usuario')
    } finally { 
      setUpdatingStatusUserId(null); 
    }
  }, []); 

  const handleConfirmStatusEdit = useCallback((userId) => {
    updateUserStatus(userId, newUserStatus);
  }, [updateUserStatus, newUserStatus]);

  const handleCancelStatusEdit = useCallback(() => { 
    setEditingStatusUserId(null); 
    setNewUserStatus('1'); 
  }, []); 

  const renderUserStatus = useCallback((usuario) => {
    const userId = usuario.us_clave;
    const currentStatus = usuario.us_status?.toString() || '1';

    if (editingStatusUserId === userId) {
      return (
        <div className={styles.editContainer}>
          <select
            value={newUserStatus}
            onChange={(e) => setNewUserStatus(e.target.value)}
            className={styles.editSelect}
            disabled={updatingStatusUserId === userId}
          >
            {Object.entries(estadosUsuario).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <div className={styles.editButtons}>
            <button
              onClick={() => handleConfirmStatusEdit(userId)}
              disabled={updatingStatusUserId === userId}
              className={`${styles.editButton} ${styles.confirmButton}`}
              title="Confirmar"
            >
              <FaCheck />
            </button>
            <button
              onClick={handleCancelStatusEdit}
              disabled={updatingStatusUserId === userId}
              className={`${styles.editButton} ${styles.cancelButton}`}
              title="Cancelar"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.userTypeContainer}>
        <span className={`${styles.userStatusLabel} ${styles[`userStatus${currentStatus}`]}`}>
          {estadosUsuario[currentStatus] || 'Activo'}
        </span>
        <button
          onClick={() => handleEditUserStatus(userId, currentStatus)}
          className={`${styles.botonAccion} ${styles.editar}`}
          title="Editar estado de usuario"
          disabled={updatingStatusUserId === userId}
        >
          <IoMdCreate />
        </button>
      </div>
    );
  }, [editingStatusUserId, newUserStatus, updatingStatusUserId, handleEditUserStatus, handleConfirmStatusEdit, handleCancelStatusEdit]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX');
  }, []);

  const usuariosFiltrados = useMemo(() => {
    const searchTermLower = searchTerm.toLowerCase();
    
    return usuarios.filter(usuario => {
      // Filtro por tipo o estado
      if (filtroTipo) {
        if (filtroTipo === 'activo' && usuario.us_status?.toString() !== '1') {
          return false;
        }
        if (filtroTipo === 'inactivo' && usuario.us_status?.toString() !== '0') {
          return false;
        }
        if (['1', '2', '3', '4'].includes(filtroTipo) && usuario.us_tipo?.toString() !== filtroTipo) {
          return false;
        }
      }
      
      // Filtro por término de búsqueda
      if (searchTerm) {
        const nombreCompleto = `${usuario.us_nombre || ''} ${usuario.us_appa || ''} ${usuario.us_apma || ''}`.toLowerCase();
        const correo = (usuario.us_correo || '').toLowerCase();
        const bachillerato = (usuario.usp_bachillerato || '').toLowerCase();
        const institucion = (usuario.usp_institucion || '').toLowerCase();
        
        return nombreCompleto.includes(searchTermLower) ||
                correo.includes(searchTermLower) ||
                bachillerato.includes(searchTermLower) ||
                institucion.includes(searchTermLower);
      }
      
      return true;
    });
  }, [usuarios, searchTerm, filtroTipo]);

  const fetchUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      
      const url = `${process.env.REACT_APP_API_URL}/admin/usuarios-completos`;
      
      const response = await fetch(url, {
        method: 'GET', 
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: abortControllerRef.current.signal
      });

      if(response.status === 401) { 
        clearAuth(); 
        throw new Error('Token inválido o expirado.');
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setUsuarios(data);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error al obtener usuarios:', err);
        setError('Error al cargar los usuarios. Por favor, intenta de nuevo más tarde.');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const updateUserType = useCallback (async (userId, newType) => { 
    try { 
      setUpdatingUserId(userId); 
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/usuarios/${userId}/tipo`, { 
        method: 'PUT', 
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }, 
        body: JSON.stringify({tipo: parseInt(newType)})
      }); 
      if (!response.ok) { 
        throw new Error('Error al actualizar usuario'); 
      }
      setUsuarios(prevUsuarios => 
        prevUsuarios.map(usuario => 
          usuario.us_clave === userId 
          ? { ...usuario, us_tipo: parseInt(newType)}
          : usuario
        )
      );
      setEditingUserId(null); 
      setNewUserType(''); 
    } catch (error) { 
      console.error('Error al actualizar tipo de usuario: ', error); 
      setError('Error al actualizar el usuario'); 
    } finally { 
      setUpdatingUserId(null); 
    }
  }, []); 

  useEffect(() => {
    fetchUsuarios();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchUsuarios]);

  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(usuariosFiltrados.length / usuariosPorPagina);
    const indexOfLastUsuario = currentPage * usuariosPorPagina;
    const indexOfFirstUsuario = indexOfLastUsuario - usuariosPorPagina;
    const usuariosActuales = usuariosFiltrados.slice(indexOfFirstUsuario, indexOfLastUsuario);
    
    return {
      totalPages,
      indexOfLastUsuario,
      indexOfFirstUsuario,
      usuariosActuales
    };
  }, [usuariosFiltrados, currentPage, usuariosPorPagina]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleTipoChange = useCallback((tipo) => {
    setFiltroTipo(tipo);
    setCurrentPage(1);
    setShowFilterMenu(false);
  }, []);

  // Nueva función para manejar el cambio de filtro de estado
  const handleEstadoChange = useCallback((estado) => {
    setFiltroTipo(estado);
    setCurrentPage(1);
    setShowFilterMenu(false);
  }, []);

  const toggleFilterMenu = useCallback(() => {
    setShowFilterMenu(!showFilterMenu);
  }, [showFilterMenu]);

  const paginate = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalUsuario(false);
  }, []);

  const handleOpenModal = useCallback(() => {
    setModalUsuario(true);
  }, []);

  const handleEditUserType = useCallback((userId, currentType) => { 
    setEditingUserId(userId); 
    setNewUserType((currentType || 1).toString()); 
  }, []); 

  const handleCancelEdit = useCallback(() => { 
    setEditingUserId(null); 
    setNewUserType(''); 
  }, []); 

  const handleConfirmEdit = useCallback((userId) => { 
    if (newUserType && newUserType !== '') {
      updateUserType(userId, newUserType); 
    }
  }, [newUserType, updateUserType]); 

  const renderFullName = useCallback((usuario) => {
    return `${usuario.us_nombre || ''} ${usuario.us_appa || ''} ${usuario.us_apma || ''}`.trim();
  }, []);

  const renderUserType = useCallback((usuario) => {
    const userId = usuario.us_clave; 
    const currentType = usuario.us_tipo || 1;

    if (editingUserId === userId) { 
      return ( 
        <div className={styles.editContainer}>
          <select 
            value={newUserType} 
            onChange={(e) => setNewUserType(e.target.value)}
            className={styles.editSelect} 
            disabled={updatingUserId === userId}
          >
            {Object.entries(tiposUsuario).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <div className={styles.editButtons}>
            <button 
              onClick={() => handleConfirmEdit(userId)}
              disabled={updatingUserId === userId} 
              className={`${styles.editButton} ${styles.confirmButton}`}
              title="Confirmar"
            >
              <FaCheck /> 
            </button>
            <button
              onClick={handleCancelEdit} 
              disabled = {updatingUserId === userId} 
              className={`${styles.editButton} ${styles.cancelButton}`}
              title = "Cancelar"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      ); 
    }
    return ( 
      <div className={styles.userTypeContainer}> 
        <span className={`${styles.userTypeLabel} ${styles[`userType${currentType}`]}`}>
          {tiposUsuario[currentType] || 'Estudiante'}
        </span>
        <button
          onClick={() => handleEditUserType(userId, currentType)}
          className={`${styles.botonAccion} ${styles.editar}`}
          title= "Editar tipo de usuario"
          disabled={updatingUserId === userId} 
        >
          <IoMdCreate />
        </button>
      </div>
    ); 
  }, [editingUserId, newUserType, updatingUserId, handleEditUserType, handleConfirmEdit, handleCancelEdit, tiposUsuario]); 

  // Función actualizada para mostrar el texto del botón de filtro
  const getFilterButtonText = useCallback(() => {
    if (filtroTipo === '1') return 'Estudiantes';
    if (filtroTipo === '2') return 'Administradores';
    if (filtroTipo === '3') return 'Tutores';
    if (filtroTipo === 'activo') return 'Activos';
    if (filtroTipo === 'inactivo') return 'Inactivos';
    return 'Filtrar';
  }, [filtroTipo]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  if (error) {
      return (
          <div className={styles.errorAlert} role="alert">
              <strong>Error: </strong>
              <span>{error}</span>
          </div>
        );
  }

  const { totalPages, indexOfLastUsuario, indexOfFirstUsuario, usuariosActuales } = paginationData;

  return (
    <div className={styles.tablaContainer}>
      <h2 className={styles.tablaTitulo}>Gestión de Usuarios</h2>

      <div className={styles.searchExportContainer}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Buscar por nombre, correo, bachillerato o institución..."
            className={styles.searchInput}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          
          <div className={styles.filterContainer}>
            <button
              className={`${styles.filterButton} ${filtroTipo ? styles.filterButtonActive : ''}`}
              onClick={toggleFilterMenu}
              aria-label="Filtrar por tipo de usuario"
            >
              <FaFilter className={styles.filterIcon} />
              {getFilterButtonText()}
            </button>
            
            {showFilterMenu && (
              <div className={styles.filterMenu}>
                <button
                  className={`${styles.filterOption} ${filtroTipo === '' ? styles.filterActive : ''}`}
                  onClick={() => handleTipoChange('')}
                >
                  Todos
                </button>
                <button
                  className={`${styles.filterOption} ${filtroTipo === '1' ? styles.filterActive : ''}`}
                  onClick={() => handleTipoChange('1')}
                >
                  Estudiantes
                </button>
                <button
                  className={`${styles.filterOption} ${filtroTipo === '2' ? styles.filterActive : ''}`}
                  onClick={() => handleTipoChange('2')}
                >
                  Administradores
                </button>
                <button
                  className={`${styles.filterOption} ${filtroTipo === '3' ? styles.filterActive : ''}`}
                  onClick={() => handleTipoChange('3')}
                >
                  Tutores
                </button>
                <button
                  className={`${styles.filterOption} ${filtroTipo === 'activo' ? styles.filterActive : ''}`}
                  onClick={() => handleEstadoChange('activo')}
                >
                  Activos
                </button>
                <button
                  className={`${styles.filterOption} ${filtroTipo === 'inactivo' ? styles.filterActive : ''}`}
                  onClick={() => handleEstadoChange('inactivo')}
                >
                  Inactivos
                </button>
              </div>
            )}
          </div>

        </div>
        
        <div className={styles.buttonContainer}>
          <ExportXLSX/>

          <button className={styles.botonAdd} onClick={handleOpenModal}>
            <IoIosAddCircleOutline/>
            Añadir usuario
          </button>

        </div>
      </div>

      <AniadirUsuario
        abrirModal={modalUsuario}
        cerrarModal={handleCloseModal}
        onUsuarioCreado={fetchUsuarios}
      />

      <div className={styles.tablaOverflow}>
        <table className={styles.tablaUsuarios}>
          <thead className={styles.tablaHeader}>
            <tr>
              <th className={styles.tablaCelda}>ID</th>
              <th className={styles.tablaCelda}>Nombre completo</th>
              <th className={styles.tablaCelda}>Correo</th>
              <th className={styles.tablaCelda}>CURP</th>
              <th className={styles.tablaCelda}>Fecha de nacimiento</th>
              <th className={styles.tablaCelda}>Estado</th>
              <th className={styles.tablaCelda}>Municipio</th>
              <th className={styles.tablaCelda}>Bachillerato</th>
              <th className={styles.tablaCelda}>Matrícula</th>
              <th className={styles.tablaCelda}>Institución</th>
              <th className={styles.tablaCelda}>Teléfono</th>
              <th className={styles.tablaCelda}>Correo del tutor</th>
              <th className={styles.tablaCelda}>Tipo de usuario</th>
              <th className={styles.tablaCelda}>Activo</th>
            </tr>
          </thead>
          <tbody>
            {usuariosActuales.length > 0 ? (
              usuariosActuales.map((usuario) => (
                <tr key={usuario.us_clave} className={styles.tablaFila}>
                  <td className={styles.tablaCelda}>{usuario.us_clave}</td>
                  <td className={styles.tablaCelda}>
                    {renderFullName(usuario)}
                  </td>
                  <td className={styles.tablaCelda}>{usuario.us_correo || '-'}</td>
                  <td className={styles.tablaCelda}>{usuario.usp_curp || '-'}</td>
                  <td className={styles.tablaCelda}>{formatDate(usuario.usp_fecha_nacimiento) || '-'}</td>
                  <td className={styles.tablaCelda}>{usuario.usp_estado || '-'}</td>
                  <td className={styles.tablaCelda}>{usuario.usp_municipio || '-'}</td>
                  <td className={styles.tablaCelda}>{usuario.usp_bachillerato || '-'}</td>
                  <td className={styles.tablaCelda}>{usuario.usp_matricula || '-'}</td>
                  <td className={styles.tablaCelda}>{usuario.usp_institucion || '-'}</td>
                  <td className={styles.tablaCelda}>{usuario.usp_telefono || '-'}</td>
                  <td className={styles.tablaCelda}>{usuario.usp_correo_tutor || '-'}</td>
                  <td className={styles.tablaCelda}>
                    {renderUserType(usuario)}
                  </td>
                  <td className={styles.tablaCelda}>
                    {renderUserStatus(usuario)} 
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="14" className={styles.tablaNoResultados}>
                  {searchTerm && !loading ? 
                    'No se encontraron usuarios que coincidan con la búsqueda' : 
                    'No hay usuarios disponibles'
                  }
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {usuariosFiltrados.length > 0 && (
        <div className={styles.paginacionContainer}>
          <div>
            <p className={styles.paginacionInfo}>
              Mostrando {indexOfFirstUsuario + 1} - {Math.min(indexOfLastUsuario, usuariosFiltrados.length)} de {usuariosFiltrados.length} usuarios
            </p>
          </div>
          <div className={styles.paginacionControles}>
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`${styles.paginacionBoton} ${currentPage === 1 ? styles.deshabilitado : styles.primario}`}
              aria-label="Página anterior"
            >
              <FaArrowLeft />
            </button>
            <select
              className={styles.paginacionSelect}
              value={currentPage}
              onChange={(e) => paginate(Number(e.target.value))}
              aria-label="Seleccionar página"
            >
              {Array.from({ length: totalPages }).map((_, index) => (
                <option key={index} value={index + 1}>
                  {index + 1}
                </option>
              ))}
            </select>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`${styles.paginacionBoton} ${
                currentPage === totalPages ? styles.deshabilitado : styles.primario
              }`}
              aria-label="Página siguiente"
            >
              <FaArrowRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TablaUsuarios;